import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { offeringWords } from "@/lib/offering-words";
import { PageHeader, Button, Badge } from "@/components/ui/PageHeader";
import { TextAreaField } from "@/components/ui/FormFields";
import * as providers from "@/api/providers";
import { uploadImage } from "@/api/upload";
import { ProviderDraftsPanel } from './ProviderDraftsPanel';
import { ProviderCampaignsPanel } from './ProviderCampaignsPanel';
import { BusinessTeamPanel } from './BusinessTeamPanel';
import { OwnerPresentationPanel } from './OwnerPresentationPanel';
import { OwnerReachPanel } from './OwnerReachPanel';
import { useAuth } from '@/context/AuthContext';

// ═══════════════════════════════════════════════════════════════════
// The shop's own desk.
//
// The dashboard schema is unusual in this API: it carries explicit RENDERING
// instructions, and they are contracts rather than suggestions. Three are
// honoured here and each is worth knowing:
//
//   · LEADS RENDER FIRST, ALWAYS. Even with nothing in them, the block stays
//     put and says "you're all caught up". Hiding it on a quiet day moves the
//     leads somewhere else, and an owner only learns where leads live if they
//     are always in the same place.
//   · The lead cards arrive UNREAD FIRST then newest. They are not re-sorted
//     here. The server already decided the order and it means something.
//   · When `profileViewsTracked` is false the view count is HIDDEN, not shown
//     as zero. No client writes views yet, so a zero would be a lie with a
//     number on it rather than a measurement.
// ═══════════════════════════════════════════════════════════════════

const TABS = ["dashboard", "updates", "offerings", "campaigns"] as const;
type Tab = (typeof TABS)[number];

export default function ProviderDeskLab() {
  const { user } = useAuth();
  const [providerId, setProviderId] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>("dashboard");

  const { data, isLoading } = useQuery({
    queryKey: ["my-providers", user?.id],
    queryFn: providers.getMyProviders,
  });

  const mine = data?.data ?? [];
  const current = mine.find((p) => p.id === providerId);
  // What this trade calls the things it offers. A pandit lists services, a
  // sweet shop lists products, and both are the same table underneath.
  // `category.kind` — goods / service / both. NOT `current.kind`, which is
  // individual vs business. Same word, different question, and getting it
  // wrong shows a pandit a grocery interface.
  const words = offeringWords(current?.category.kind);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Shop desk"
        subtitle="What a business sees about itself: who is asking, what it has said, what it sells."
        breadcrumbs={[
          { label: "Dashboard", href: "/" },
          { label: "Providers", href: "/lab/providers" },
          { label: "Desk" },
        ]}
      />

      <ProviderDraftsPanel key={user?.id} />
      <BusinessTeamPanel key={'team:' + user?.id} providerId={providerId} />
      <section className="bg-white border border-gray-200 rounded-2xl p-5">
        <h2 className="text-sm font-medium text-gray-900 mb-1">Businesses you have claimed</h2>
        <p className="text-xs text-gray-500 mb-3">
          Owner-only. A listing nobody has claimed has no desk — which is what
          makes claiming worth doing.
        </p>
        {isLoading ? (
          <p className="text-sm text-gray-500">Loading…</p>
        ) : !mine.length ? (
          <p className="text-sm text-gray-500">
            You have not claimed a listing. The seeded businesses are real and
            deliberately unclaimed; the demo shop is the one to use here.
          </p>
        ) : (
          <div className="flex gap-2 flex-wrap">
            {mine.map((p) => (
              <button
                key={p.id}
                onClick={() => setProviderId(p.id)}
                className={`px-3 py-1.5 rounded-lg text-sm border text-left ${
                  providerId === p.id
                    ? "border-gray-900 bg-gray-50"
                    : "border-gray-200 hover:bg-gray-50"
                }`}
              >
                {p.name}
                <span className="text-gray-400 text-xs">
                  {" "}
                  · {p.category.name} · {p.city}
                </span>
              </button>
            ))}
          </div>
        )}
      </section>

      {providerId && (
        <section className="bg-white border border-gray-200 rounded-2xl p-5">
          <div className="mb-4">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="font-semibold text-gray-900">{current?.name}</h2>
              {current?.isVerified && <Badge variant="green">verified</Badge>}
              <Badge variant={current?.acceptingWork ? "green" : "gray"}>
                {current?.acceptingWork ? "taking work" : "not taking work"}
              </Badge>
            </div>
            <div className="flex gap-1 flex-wrap mt-3 border-b border-gray-200 pb-3">
              {TABS.map((t) => (
                <button
                  key={t}
                  onClick={() => setTab(t)}
                  className={`px-3 py-1.5 rounded-lg text-sm capitalize ${
                    tab === t ? "bg-gray-900 text-white" : "text-gray-600 hover:bg-gray-100"
                  }`}
                >
                  {t === "offerings" ? words.section : t}
                </button>
              ))}
            </div>
          </div>

          {tab === "dashboard" && <Dashboard id={providerId} />}
          {tab === "dashboard" && <OwnerReachPanel id={providerId} categoryCode={current?.category.code} />}
          {tab === "updates" && <Updates id={providerId} />}
          {tab === "offerings" && <Catalogue id={providerId} kind={current?.category.kind} />}
          {tab === "campaigns" && <ProviderCampaignsPanel key={user?.id + ':' + providerId} id={providerId} />}
          <OwnerPresentationPanel key={'owner:' + user?.id + ':' + providerId} providerId={providerId} />
        </section>
      )}
    </div>
  );
}

function Stat({ label, value, hint }: { label: string; value: number | string; hint?: string }) {
  return (
    <div>
      <div className="text-2xl font-semibold text-gray-900 tabular-nums">{value}</div>
      <div className="text-xs text-gray-500">{label}</div>
      {hint && <div className="text-xs text-gray-400">{hint}</div>}
    </div>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <p className="text-sm text-gray-500">{children}</p>;
}

// ── Dashboard ──────────────────────────────────────────────────────

function Dashboard({ id }: { id: string }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ["provider-dashboard", id],
    queryFn: () => providers.getProviderDashboard(id),
    retry: false,
  });

  if (isLoading) return <Empty>Loading…</Empty>;
  if (error)
    return (
      <p className="text-sm text-red-700">
        {error instanceof Error ? error.message : "Could not read the dashboard"}
      </p>
    );

  const d = data?.data;
  if (!d) return <Empty>No dashboard.</Empty>;

  return (
    <div className="space-y-6">
      {/*
        FIRST, ALWAYS. Not conditional on there being anything in it — the
        empty state is part of the contract, because a block that moves on a
        quiet day never teaches anyone where to look.
      */}
      <div>
        <div className="flex items-center gap-2 mb-1">
          <p className="text-sm font-medium text-gray-900">Someone is asking</p>
          {d.potentialLeads.unread > 0 && (
            <Badge variant="green">{d.potentialLeads.unread} unread</Badge>
          )}
        </div>
        {!d.potentialLeads.items.length ? (
          <p className="text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded px-3 py-2">
            You're all caught up. This block stays here whether or not anything
            is in it, so leads are always in the same place.
          </p>
        ) : (
          <ul className="space-y-2">
            {/* Server order: unread first, then newest. Not re-sorted. */}
            {d.potentialLeads.items.map((l) => (
              <li
                key={l.threadId}
                className={`border rounded-xl px-4 py-3 text-sm ${
                  l.isUnread ? "border-gray-900 bg-gray-50" : "border-gray-200"
                }`}
              >
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-gray-900">{l.from.name}</span>
                  {l.from.city && <span className="text-xs text-gray-500">{l.from.city}</span>}
                  {l.isUnread && <Badge variant="green">unread</Badge>}
                  <span className="text-xs text-gray-400 ml-auto">
                    {new Date(l.lastMessageAt).toLocaleDateString()}
                  </span>
                </div>
                <p className="text-gray-700 mt-1">{l.excerpt}</p>
              </li>
            ))}
          </ul>
        )}
        <p className="text-xs text-gray-400 mt-1">
          {d.potentialLeads.openThreads} open thread
          {d.potentialLeads.openThreads === 1 ? "" : "s"} · capped at ten here;
          the inbox holds the rest
        </p>
      </div>

      <div className="border-t border-gray-200 pt-4">
        <p className="text-sm font-medium text-gray-900 mb-2">Keep it alive</p>
        <div className="flex gap-6 flex-wrap mb-2">
          <Stat label="updates posted" value={d.keepItAlive.updatesPosted} />
          <Stat
            label="days since last"
            value={d.keepItAlive.daysSinceLastUpdate ?? "never"}
          />
        </div>
        {d.keepItAlive.listingLooksStale && (
          <p className="text-sm text-yellow-800 bg-yellow-50 border border-yellow-200 rounded px-3 py-2 mb-2">
            Nothing posted in thirty days, or ever. A listing that never speaks
            is one a reader cannot tell is still trading.
          </p>
        )}
        {d.keepItAlive.incompleteFields.length > 0 && (
          <div>
            <p className="text-xs text-gray-500 mb-1">
              Blank fields that cost enquiries — a to-do list, not a scolding:
            </p>
            <div className="flex gap-1.5 flex-wrap">
              {d.keepItAlive.incompleteFields.map((f) => (
                <Badge key={f} variant="yellow">
                  {f.replace(/([A-Z])/g, " $1").toLowerCase()}
                </Badge>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="border-t border-gray-200 pt-4">
        <p className="text-sm font-medium text-gray-900 mb-2">Reach</p>
        <div className="flex gap-6 flex-wrap">
          <Stat label="followers" value={d.reach.followers} />
          <Stat label="new in 30d" value={d.reach.followersLast30d} />
          <Stat
            label="next update reaches"
            value={d.reach.notifiableFollowers}
            hint="forward-looking"
          />
          {/*
            HIDDEN, not zero. No client writes profile views yet, so the count
            is structurally zero — rendering it would report a lie as a
            measurement.
          */}
          {d.reach.profileViewsTracked ? (
            <Stat label="views in 30d" value={d.reach.profileViews30d} />
          ) : (
            <div className="text-xs text-gray-400 self-end pb-1 max-w-[16rem]">
              Views are not tracked yet — no client records them, so the metric
              is hidden rather than shown as zero.
            </div>
          )}
        </div>
      </div>

      <div className="border-t border-gray-200 pt-4">
        <p className="text-sm font-medium text-gray-900 mb-2">Trust</p>
        <div className="flex gap-6 flex-wrap">
          <Stat label="vouches" value={d.trust.vouches} />
          <Stat label="new in 30d" value={d.trust.vouchesLast30d} />
          <Stat label="rating" value={d.trust.avgRating || "—"} />
          <Stat label="named in answers" value={d.trust.mentionsInAnswers} />
        </div>
        {d.trust.verifications.length > 0 && (
          <div className="flex gap-1.5 flex-wrap mt-2">
            {d.trust.verifications.map((v) => (
              <Badge key={v} variant="green">
                {v.replace(/_/g, " ")}
              </Badge>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Updates ────────────────────────────────────────────────────────

function Updates({ id }: { id: string }) {
  const qc = useQueryClient();
  const [body, setBody] = useState("");
  const [type, setType] = useState<string>("general");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["provider-updates", id],
    queryFn: () => providers.listProviderUpdates(id, { limit: 20 }),
  });
  const updates = data?.data.updates ?? [];

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setErr(null);
    try {
      await fn();
      await qc.invalidateQueries({ queryKey: ["provider-updates", id] });
      await qc.invalidateQueries({ queryKey: ["provider-dashboard", id] });
    } catch (e) {
      setErr(e instanceof Error ? e.message : "That did not work");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <p className="text-xs text-gray-500 mb-3">
        What the shop tells the people following it. An{" "}
        <code>offer</code> and an <code>availability</code> note are different
        promises, which is why the type is not decoration.
      </p>

      <div className="mb-4 space-y-2">
        <select
          value={type}
          onChange={(e) => setType(e.target.value)}
          className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm"
        >
          {providers.PROVIDER_UPDATE_TYPES.map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
        <TextAreaField
          label=""
          rows={2}
          placeholder="Fresh batch of kaju katli in from tomorrow…"
          value={body}
          onChange={(e) => setBody(e.target.value)}
        />
        <Button
          size="sm"
          isLoading={busy}
          disabled={!body.trim()}
          onClick={() =>
            run(async () => {
              await providers.createProviderUpdate(id, { body: body.trim(), type });
              setBody("");
            })
          }
        >
          Post update
        </Button>
        {err && <p className="text-sm text-red-700">{err}</p>}
      </div>

      {isLoading ? (
        <Empty>Loading…</Empty>
      ) : !updates.length ? (
        <Empty>Nothing posted yet.</Empty>
      ) : (
        <ul className="space-y-2">
          {updates.map((u) => (
            <li key={u.id} className="border border-gray-200 rounded-xl px-4 py-3 text-sm">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant="blue">{u.type}</Badge>
                {/* Null expiry is evergreen, not missing data. */}
                {u.expiresAt ? (
                  <Badge variant="yellow">
                    until {new Date(u.expiresAt).toLocaleDateString()}
                  </Badge>
                ) : (
                  <Badge variant="gray">evergreen</Badge>
                )}
                <span className="text-xs text-gray-400">
                  {new Date(u.createdAt).toLocaleDateString()}
                </span>
                <button
                  disabled={busy}
                  onClick={() => run(() => providers.deleteProviderUpdate(id, u.id))}
                  className="text-xs text-red-700 ml-auto disabled:opacity-50"
                >
                  delete
                </button>
              </div>
              <p className="text-gray-700 mt-1">{u.body}</p>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

// ── Catalogue ──────────────────────────────────────────────────────

function Catalogue({ id, kind }: { id: string; kind?: string }) {
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [price, setPrice] = useState("");
  const [until, setUntil] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // The owner's view, so drafts are included. Without this a hidden item
  // vanishes from the owner's own screen and can never be brought back.
  const { data, isLoading } = useQuery({
    queryKey: ["provider-items", id],
    queryFn: () => providers.listProviderItems(id, true),
  });
  const items = data?.data.items ?? [];
  const words = offeringWords(kind);

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setErr(null);
    try {
      await fn();
      await qc.invalidateQueries({ queryKey: ["provider-items", id] });
    } catch (e) {
      setErr(e instanceof Error ? e.message : "That did not work");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <p className="text-xs text-gray-500 mb-3">
        {words.blurb} Prices are free text you wrote: there is no checkout, so
        this is a claim you make and never an amount we charge.
      </p>

      <div className="mb-4 flex gap-2 flex-wrap items-center">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={words.namePlaceholder}
          className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm"
        />
        <input
          value={price}
          onChange={(e) => setPrice(e.target.value)}
          placeholder={words.pricePlaceholder}
          aria-label={words.priceLabel}
          className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm"
        />
        {/* Optional: the last day it is offered ("Rakhi hamper" until Raksha Bandhan). */}
        <label className="text-xs text-gray-600">
          Until{" "}
          <input
            type="date"
            value={until}
            onChange={(e) => setUntil(e.target.value)}
            className="border border-gray-300 rounded-lg px-2 py-1 text-sm"
          />
        </label>
        {/* Optional. Uploaded with category=provider: the item takes only the owner's own ready photo. */}
        <label className="text-xs text-gray-600">
          Photo{" "}
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            disabled={busy}
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = "";
              if (file) void run(async () => setPhotoUrl((await uploadImage(file, "provider")).url));
            }}
          />
          {photoUrl && <span className="ml-1">attached</span>}
        </label>
        <Button
          size="sm"
          isLoading={busy}
          disabled={!name.trim()}
          onClick={() =>
            run(async () => {
              await providers.createProviderItem(id, {
                name: name.trim(),
                priceInfo: price.trim() || undefined,
                imageUrl: photoUrl ?? undefined,
                availableUntil: until || undefined,
              });
              setName("");
              setPrice("");
              setUntil("");
              setPhotoUrl(null);
            })
          }
        >
          Add
        </Button>
        {err && <p className="text-sm text-red-700">{err}</p>}
      </div>

      {isLoading ? (
        <Empty>Loading…</Empty>
      ) : !items.length ? (
        <Empty>{`No ${words.singular}s listed yet.`}</Empty>
      ) : (
        <ul className="space-y-2">
          {items.map((it) => (
            <li
              key={it.id}
              className="border border-gray-200 rounded-xl px-4 py-2.5 text-sm flex items-center gap-2 flex-wrap"
            >
              <span className="font-medium text-gray-900">{it.name}</span>
              {it.priceInfo && <Badge variant="blue">{it.priceInfo}</Badge>}
              {/* Inactive is the shop's own draft state, not a deletion. */}
              {!it.isActive && <Badge variant="gray">hidden</Badge>}
              {it.availableUntil && <Badge variant="gray">until {it.availableUntil}</Badge>}
              <span className="ml-auto flex gap-3">
                <button
                  disabled={busy}
                  onClick={() =>
                    run(() =>
                      providers.updateProviderItem(id, it.id, { isActive: !it.isActive }),
                    )
                  }
                  className="text-xs text-gray-600 disabled:opacity-50"
                >
                  {it.isActive ? "hide" : "show"}
                </button>
                <button
                  disabled={busy}
                  onClick={() => {
                    // Empty offers it all year again; Cancel changes nothing.
                    const day = window.prompt("Last day it is offered (YYYY-MM-DD, empty for all year)", it.availableUntil ?? "");
                    if (day === null) return;
                    void run(() => providers.updateProviderItem(id, it.id, { availableUntil: day.trim() || null }));
                  }}
                  className="text-xs text-gray-600 disabled:opacity-50"
                >
                  last day
                </button>
                <button
                  disabled={busy}
                  onClick={() => run(() => providers.deleteProviderItem(id, it.id))}
                  className="text-xs text-red-700 disabled:opacity-50"
                >
                  delete
                </button>
              </span>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}
